import * as THREE from 'three/webgpu';
import {CSS2DObject} from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchPointObject,
    isSketchSlotObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../../../mesh/guards';
import type {SketchGroup} from '../../../mesh/sketch/SketchGroup';
import {EllipseControlPointRole} from '../../../mesh/sketch/ISketchObject';
import type {SketchDimensionRepository} from '../repository/SketchDimensionRepository';
import type {SketchDimensionSnapshot} from '../SketchDimensionTypes';
import {SketchDimensionHitProxy} from './SketchDimensionHitProxy';
import type {SketchDimensionHitProxyCallbacks} from './SketchDimensionHitProxy';
import {resolveSketchDimensionLayout} from './SketchDimensionLayoutResolver';
import type {
    SketchDimensionLayout,
    SketchDimensionLayoutSegment,
    SketchDimensionLayoutVec2,
    SketchDimensionReferenceAnchor,
} from './SketchDimensionLayoutResolver';
import {isSketchDimensionInstant2dSupported} from './SketchDimensionInstant2dResolver';
import {resolveSketchDimensionStateColor} from './SketchDimensionStyleResolver';
import {formatSketchDimensionDisplayValue} from './SketchDimensionTextFormatter';
import {markCadObjectAsRuntimeDerived} from '../../../document/CadObjectPersistence';
import {SketchDimensionGeometryBuffer} from './geometry';

/** 选中高亮色（SOLIDWORKS 风格绿色）。 */
const SELECTED_COLOR = '#1eae4b';

/** 角度弧采样段数。 */
const ARC_SAMPLE_SEGMENTS = 24;

/** 无相机换算时的箭头兜底世界长度（米）。 */
const DEFAULT_ARROW_WORLD_LENGTH = 0.0025;

/** 实心箭头半宽与长度之比（对齐 SOLIDWORKS 细长箭头）。 */
const ARROW_HALF_WIDTH_RATIO = 0.32;

/** 渲染器挂载选项。 */
export interface SketchDimensionAnnotationRendererAttachOptions {
    /** 宿主场景。 */
    readonly scene: THREE.Scene;
    /** 尺寸仓库。 */
    readonly repository: SketchDimensionRepository;
    /** 草图组解析（sketchId → 运行时组；不在场景时返回 null）。 */
    readonly resolveSketchGroup: (sketchId: string) => SketchGroup | null;
    /** 标签命中回调；缺省时标注不可交互（纯显示）。 */
    readonly hitCallbacks?: SketchDimensionHitProxyCallbacks;
    /**
     * 解析箭头的世界尺寸（屏幕定长：像素 × 每像素世界长）。
     *
     * 缺省时退回固定几何尺寸（`DEFAULT_ARROW_WORLD_LENGTH`），小图形上箭头会偏大。
     */
    readonly resolveArrowWorldLength?: () => number;
}

/**
 * 持久尺寸标注渲染器（阶段 5：Renderer + Layout + 标签 HitProxy 装配）。
 *
 * 设计说明：
 * 1. 渲染对象全部 runtime-derived：仓库事件驱动增量增删改；
 *    几何变更（拖点/求解写回）由宿主调 `refreshSketch` 重算布局；
 * 2. 布局由 `resolveSketchDimensionLayout` 纯函数产出（尺寸线/界线/箭头/角度弧），
 *    引用锚点按持久 ID 从草图组索引解析（`getObjectByPersistentId`），
 *    悬空引用显式降级为仅文字；
 * 3. 状态色（蓝/黑/灰/红/黄/棕）来自 `resolveSketchDimensionStateColor`，
 *    选中态覆盖为绿色；线材质按颜色缓存复用；
 * 4. 拖动标注：`previewLabelPosition` 临时覆盖布局（不写仓库），
 *    mouseup 由宿主提交 `updateDisplay` 事务后经仓库事件正式刷新；
 * 5. dispose 释放全部 Geometry/Material/DOM/订阅。
 */
export class SketchDimensionAnnotationRenderer {
    /** 标注容器。 */
    private readonly group = new THREE.Group();

    /** 尺寸 ID 到渲染条目映射。 */
    private readonly entryById = new Map<string, SketchDimensionAnnotationEntry>();

    /** 线材质缓存（按颜色）。 */
    private readonly materialByColor = new Map<string, THREE.LineBasicMaterial>();

    /** 实心箭头材质缓存（按颜色）。 */
    private readonly arrowMaterialByColor = new Map<string, THREE.MeshBasicMaterial>();

    /** 拖动预览的标注位置覆盖。 */
    private readonly previewLabelById = new Map<string, SketchDimensionLayoutVec2>();

    /** Instant2D 拖动预览的名义值覆盖（只影响标签文本，不写仓库）。 */
    private readonly previewValueById = new Map<string, number>();

    /** 当前选中的尺寸 ID 集合。 */
    private readonly selectedDimensionIds = new Set<string>();

    /** 当前主尺寸 ID；单尺寸属性面板与已有兼容 API 使用。 */
    private primarySelectedDimensionId: string | null = null;

    /** 当前"编辑中"草图的持久 ID；null = 无草图会话，全部尺寸隐藏（SW 语义）。 */
    private visibleSketchId: string | null = null;

    /** 仓库订阅清理函数。 */
    private unsubscribe: (() => void) | null = null;

    /** 挂载选项。 */
    private options: SketchDimensionAnnotationRendererAttachOptions | null = null;

    /** 局部到世界转换复用点，避免按尺寸顶点创建临时 Vector3。 */
    private readonly worldPoint = new THREE.Vector3();

    public constructor() {
        // 尺寸事实由草图扩展保存，线、箭头与标签只是可重建的运行时投影。
        markCadObjectAsRuntimeDerived(this.group);
        this.group.name = 'sketch-dimension-annotations';
        this.group.renderOrder = 998;
    }

    /**
     * 挂载渲染器并订阅仓库事件。
     *
     * @param options 挂载选项。
     */
    public attach(options: SketchDimensionAnnotationRendererAttachOptions): void {
        this.detach();
        this.options = options;
        options.scene.add(this.group);
        this.unsubscribe = options.repository.observe((event) => {
            if (event.type === 'deleted') {
                this.removeAnnotation(event.dimensionId);
                return;
            }
            this.upsertAnnotation(event.sketchId, event.dimensionId);
        });
    }

    /** 卸载渲染器（清空标注、材质与订阅）。 */
    public detach(): void {
        this.unsubscribe?.();
        this.unsubscribe = null;
        [...this.entryById.keys()].forEach((dimensionId) => {
            this.removeAnnotation(dimensionId);
        });
        this.materialByColor.forEach((material) => material.dispose());
        this.materialByColor.clear();
        this.arrowMaterialByColor.forEach((material) => material.dispose());
        this.arrowMaterialByColor.clear();
        this.previewLabelById.clear();
        this.previewValueById.clear();
        this.selectedDimensionIds.clear();
        this.primarySelectedDimensionId = null;
        this.options?.scene.remove(this.group);
        this.options = null;
    }

    /**
     * 设置选中尺寸（高亮为绿色）。
     *
     * @param dimensionId 尺寸持久 ID；null 清除选中。
     */
    public setSelectedDimension(dimensionId: string | null): void {
        this.setSelectedDimensions(dimensionId ? [dimensionId] : []);
    }

    /**
     * 设置尺寸选择集合。
     *
     * @param dimensionIds 尺寸持久 ID 列表；首项作为主尺寸。
     */
    public setSelectedDimensions(dimensionIds: readonly string[]): void {
        const nextIds = [...new Set(dimensionIds)];
        const nextSet = new Set(nextIds);
        const changedIds = new Set<string>();
        const previousPrimaryDimensionId = this.primarySelectedDimensionId;
        const previousSelectionSize = this.selectedDimensionIds.size;

        this.selectedDimensionIds.forEach((dimensionId) => {
            if (!nextSet.has(dimensionId)) {
                changedIds.add(dimensionId);
            }
        });
        nextSet.forEach((dimensionId) => {
            if (!this.selectedDimensionIds.has(dimensionId)) {
                changedIds.add(dimensionId);
            }
        });

        if (
            changedIds.size === 0
            && previousPrimaryDimensionId === (nextIds[0] ?? null)
        ) {
            return;
        }

        if (previousPrimaryDimensionId !== (nextIds[0] ?? null)) {
            if (previousPrimaryDimensionId) {
                changedIds.add(previousPrimaryDimensionId);
            }
            if (nextIds[0]) {
                changedIds.add(nextIds[0]);
            }
        }
        if (previousSelectionSize !== nextSet.size && previousPrimaryDimensionId) {
            // 主尺寸是否显示 Instant2D 手柄取决于是否单选，选择数量变化时也必须刷新主尺寸。
            changedIds.add(previousPrimaryDimensionId);
        }

        this.selectedDimensionIds.clear();
        nextIds.forEach((dimensionId) => this.selectedDimensionIds.add(dimensionId));
        this.primarySelectedDimensionId = nextIds[0] ?? null;
        changedIds.forEach((dimensionId) => {
            const entry = this.entryById.get(dimensionId);

            if (entry) {
                this.upsertAnnotation(entry.sketchId, dimensionId);
            }
        });
    }

    /** 当前选中尺寸 ID。 */
    public getSelectedDimension(): string | null {
        return this.primarySelectedDimensionId;
    }

    /**
     * 读取全部选中尺寸 ID。
     *
     * @returns 保持选择顺序的尺寸持久 ID 列表。
     */
    public getSelectedDimensions(): readonly string[] {
        return Array.from(this.selectedDimensionIds);
    }

    /**
     * 按框选屏幕矩形收集可见尺寸。
     *
     * 设计说明：
     * 1. 左到右窗口选择要求完整包围尺寸线、箭头和标签；
     * 2. 右到左交叉选择只要与尺寸标注屏幕范围相交即可；
     * 3. 仅查询当前编辑草图，隐藏尺寸不得进入选择集。
     *
     * @param selectionRect 相对渲染容器的框选矩形。
     * @param isCrossing 是否为右到左交叉选择。
     * @param camera 当前相机。
     * @param containerRect 渲染容器屏幕矩形。
     * @returns 命中的尺寸持久 ID 列表。
     */
    public collectDimensionsByScreenRect(
        selectionRect: {readonly minX: number; readonly minY: number; readonly maxX: number; readonly maxY: number},
        isCrossing: boolean,
        camera: THREE.Camera,
        containerRect: DOMRect,
    ): readonly string[] {
        const dimensionIds: string[] = [];

        this.entryById.forEach((entry, dimensionId) => {
            if (!this.isEntryVisible(entry)) {
                return;
            }
            const annotationRect = this.resolveEntryScreenRect(entry, camera, containerRect);
            if (!annotationRect) {
                return;
            }
            const hit = isCrossing
                ? this.screenRectsIntersect(selectionRect, annotationRect)
                : this.screenRectContains(selectionRect, annotationRect);

            if (hit) {
                dimensionIds.push(dimensionId);
            }
        });

        return dimensionIds;
    }

    /**
     * 拖动预览：临时覆盖标注位置并重算布局（不写仓库、不入历史）。
     *
     * @param dimensionId 尺寸持久 ID。
     * @param labelPosition 预览标注位置（局部）；null 清除覆盖。
     */
    public previewLabelPosition(dimensionId: string, labelPosition: SketchDimensionLayoutVec2 | null): void {
        if (labelPosition) {
            this.previewLabelById.set(dimensionId, labelPosition);
        } else {
            this.previewLabelById.delete(dimensionId);
        }

        const entry = this.entryById.get(dimensionId);

        if (entry) {
            this.upsertAnnotation(entry.sketchId, dimensionId);
        }
    }

    /**
     * Instant2D 拖动预览：临时覆盖标签显示的名义值（不写仓库、不入历史）。
     *
     * @param dimensionId 尺寸持久 ID。
     * @param nominalValue 预览名义值（核心单位）；null 清除覆盖。
     */
    public previewNominalValue(dimensionId: string, nominalValue: number | null): void {
        if (nominalValue !== null) {
            this.previewValueById.set(dimensionId, nominalValue);
        } else {
            this.previewValueById.delete(dimensionId);
        }

        const entry = this.entryById.get(dimensionId);

        if (entry) {
            this.upsertAnnotation(entry.sketchId, dimensionId);
        }
    }

    /**
     * 解析指定尺寸的引用锚点（Instant2D 值反算等宿主逻辑消费）。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @returns 锚点数组；尺寸或草图不在场景时返回 null。
     */
    public resolveAnchorsOf(
        sketchId: string,
        dimensionId: string,
    ): readonly (SketchDimensionReferenceAnchor | undefined)[] | null {
        const options = this.options;
        const snapshot = options?.repository.get(sketchId, dimensionId)?.toSnapshot() ?? null;
        const sketchGroup = options?.resolveSketchGroup(sketchId) ?? null;

        if (!snapshot || !sketchGroup) {
            return null;
        }

        return this.resolveAnchors(snapshot, sketchGroup);
    }

    /**
     * 尺寸线/界线命中查询（ARC-003：非标签区域的 WebGL 拾取）。
     *
     * @param raycaster 已按点击位置构造的射线（调用方负责设置 Line threshold）。
     * @returns 最近命中的尺寸持久 ID；未命中返回 null。
     */
    public hitTestLines(raycaster: THREE.Raycaster): string | null {
        let nearestId: string | null = null;
        let nearestDistance = Number.POSITIVE_INFINITY;

        this.entryById.forEach((entry, dimensionId) => {
            // 隐藏尺寸不参与拾取：只加约束、不显示 UI 的尺寸不能被点选/框选。
            if (!this.isEntryVisible(entry)) {
                return;
            }
            const hits = raycaster.intersectObject(entry.lines, false);
            const distance = hits[0]?.distance;

            if (distance !== undefined && distance < nearestDistance) {
                nearestDistance = distance;
                nearestId = dimensionId;
            }
        });

        return nearestId;
    }

    /**
     * 重算单个草图的全部尺寸布局（几何变更后由宿主调用）。
     *
     * @param sketchId 草图持久 ID；省略时刷新全部。
     */
    public refreshSketch(sketchId?: string): void {
        this.entryById.forEach((entry, dimensionId) => {
            if (!sketchId || entry.sketchId === sketchId) {
                this.upsertAnnotation(entry.sketchId, dimensionId);
            }
        });
    }

    /** 解析引用锚点（持久 ID → 运行时几何投影）。 */
    private resolveAnchors(
        snapshot: SketchDimensionSnapshot,
        sketchGroup: SketchGroup,
    ): readonly (SketchDimensionReferenceAnchor | undefined)[] {
        return snapshot.references.map((reference) => {
            const target = sketchGroup.getObjectByPersistentId(reference.pointId ?? reference.entityId)
                ?? sketchGroup.getObjectByPersistentId(reference.entityId);

            if (!target) {
                return undefined;
            }
            if (isSketchPointObject(target)) {
                const local = target.getLocalPosition2D();

                return {kind: 'point', position: [local.x, local.y]};
            }
            if (isSketchLineObject(target)) {
                const start = target.startPoint.getLocalPosition2D();
                const end = target.endPoint.getLocalPosition2D();

                return {kind: 'line', start: [start.x, start.y], end: [end.x, end.y]};
            }
            if (isSketchCircleObject(target)) {
                const center = target.getCenterControlPoint().getLocalPosition2D();

                return {kind: 'circle', center: [center.x, center.y], radius: target.radius};
            }
            if (isSketchArcObject(target)) {
                const center = target.getCenterPoint().getLocalPosition2D();

                return {kind: 'circle', center: [center.x, center.y], radius: target.getArcDefinition().radius};
            }
            //------ 圆弧槽口中心弧锚点 开始------
            /**
             * 圆弧槽口的中心弧不是独立实体（只有内外弧与端帽），但它正是槽口的半径参数，
             * 半径尺寸就锚在槽口本身：圆心取弧心控制点，半径取弧心到中心弧中点的距离。
             */
            if (isSketchSlotObject(target) && target.isArcSlot()) {
                const centerPoint = target.arcCenterPoint;
                const midpoint = target.centerArcMidpoint;

                if (!centerPoint || !midpoint) {
                    return undefined;
                }
                const center = centerPoint.getLocalPosition2D();
                const mid = midpoint.getLocalPosition2D();

                return {
                    kind: 'circle',
                    center: [center.x, center.y],
                    radius: Math.hypot(mid.x - center.x, mid.y - center.y),
                };
            }
            //------ 圆弧槽口中心弧锚点 结束------
            //------ 样条总长锚点 开始------
            // 样条没有可解析的"两端投影"，总长标注按采样折线引线式绘制。
            if (isSketchSplineObject(target) || isSketchStyleSplineObject(target)) {
                const points = target.getCurvePoints().map((point) => {
                    const local = target.referencePlane.worldToLocal(point.clone());

                    return [local.x, local.y] as const;
                });

                return points.length >= 2 ? {kind: 'polyline', points} : undefined;
            }
            //------ 样条总长锚点 结束------
            //------ 椭圆基准轴锚点（曲线对偏移距离） 开始------
            if (isSketchEllipseObject(target)) {
                const center = target.getCenterPoint().getLocalPosition2D();
                const major = target.getCardinalControlPointsSnapshot().get(EllipseControlPointRole.MajorPositive);

                if (!major) {
                    return undefined;
                }
                const majorLocal = major.getLocalPosition2D();
                const definition = target.getEllipseDefinition();

                // 锚点携带半短轴与弧起止角：布局层据此在弧域内解析"朝标注方向"的锚点，
                // 标注可沿椭圆弧拖动，且锚点不会落在弧没画到的角域外。
                return {
                    kind: 'ellipse',
                    center: [center.x, center.y],
                    major: [majorLocal.x, majorLocal.y],
                    semiMinor: definition.radiusY,
                    startAngle: definition.startAngle,
                    endAngle: definition.endAngle,
                    clockwise: definition.clockwise,
                };
            }
            //------ 椭圆基准轴锚点（曲线对偏移距离） 结束------
            //------ 抛物线锚点（曲线对偏移距离） 开始------
            // curve-offset-distance 的名义值在源曲线首采样段法向上测量：
            // 抛物线等距（方案A）输出样条后，源侧锚点必须取源抛物线首采样点，
            // 与样条侧首控制点（首采样点）配对，尺寸线两端与名义值测量点一致；
            // 顶点/焦点锚点仅用于抛物线自身参数标注等其他场景。
            if (isSketchParabolaObject(target)) {
                if (snapshot.dimensionKind === 'curve-offset-distance') {
                    const curvePoints = target.getCurvePoints();
                    const firstPoint = curvePoints[0];

                    if (firstPoint) {
                        const local = target.referencePlane.worldToLocal(firstPoint.clone());

                        return {kind: 'point', position: [local.x, local.y]};
                    }
                }
                const apex = target.getApexPoint().getLocalPosition2D();
                const focal = target.getFocalPoint().getLocalPosition2D();

                return {kind: 'parabola', apex: [apex.x, apex.y], focal: [focal.x, focal.y]};
            }
            //------ 抛物线锚点（曲线对偏移距离） 结束------

            return undefined;
        });
    }

    /** 取或建线材质（按颜色缓存）。 */
    private materialOf(color: string): THREE.LineBasicMaterial {
        let material = this.materialByColor.get(color);

        if (!material) {
            material = new THREE.LineBasicMaterial({color, depthTest: false});
            this.materialByColor.set(color, material);
        }

        return material;
    }

    /** 取或建实心箭头材质（按颜色缓存）。 */
    private arrowMaterialOf(color: string): THREE.MeshBasicMaterial {
        let material = this.arrowMaterialByColor.get(color);

        if (!material) {
            material = new THREE.MeshBasicMaterial({color, depthTest: false, side: THREE.DoubleSide});
            this.arrowMaterialByColor.set(color, material);
        }

        return material;
    }

    /** 当前箭头世界长度（屏幕定长，宿主按相机换算；缺省退回固定几何尺寸）。 */
    private resolveArrowWorldLength(): number {
        const resolved = this.options?.resolveArrowWorldLength?.();

        return resolved !== undefined && Number.isFinite(resolved) && resolved > 0
            ? resolved
            : DEFAULT_ARROW_WORLD_LENGTH;
    }

    /** 把箭头布局写入实心三角面缓冲（局部 → 世界）。 */
    private applyArrowMesh(entry: SketchDimensionAnnotationEntry, color: string): void {
        const referencePlane = entry.referencePlane;

        if (!referencePlane || entry.arrows.length === 0) {
            entry.arrowBuffer.beginUpdate(0);
            entry.arrowMesh.geometry = entry.arrowBuffer.endUpdate();
            entry.arrowMesh.visible = false;
            return;
        }

        const length = this.resolveArrowWorldLength();
        const halfWidth = length * ARROW_HALF_WIDTH_RATIO;
        const position = entry.arrowBuffer.beginUpdate(entry.arrows.length * 3);

        entry.arrows.forEach((arrow, index) => {
            const directionX = arrow.direction[0];
            const directionY = arrow.direction[1];
            // 尖端在锚点、底边两角沿反向张开 = 实心三角箭头（指向测量端点）。
            const tip = arrow.position;
            const baseX = tip[0] + directionX * length;
            const baseY = tip[1] + directionY * length;
            referencePlane.localToWorldTo(tip[0], tip[1], this.worldPoint);
            position.setXYZ(index * 3, this.worldPoint.x, this.worldPoint.y, this.worldPoint.z);
            referencePlane.localToWorldTo(baseX - directionY * halfWidth, baseY + directionX * halfWidth, this.worldPoint);
            position.setXYZ(index * 3 + 1, this.worldPoint.x, this.worldPoint.y, this.worldPoint.z);
            referencePlane.localToWorldTo(baseX + directionY * halfWidth, baseY - directionX * halfWidth, this.worldPoint);
            position.setXYZ(index * 3 + 2, this.worldPoint.x, this.worldPoint.y, this.worldPoint.z);
        });
        entry.arrowMesh.geometry = entry.arrowBuffer.endUpdate();
        entry.arrowMesh.material = this.arrowMaterialOf(color);
        entry.arrowMesh.visible = this.isEntryVisible(entry);
    }

    //------ 尺寸随草图会话显隐 开始------

    /**
     * 设置当前"编辑中"的草图（SW 语义：尺寸只在编辑对应草图时显示）。
     *
     * 传 null 表示没有草图在编辑，全部尺寸隐藏。
     * 隐藏是显示层状态，不动仓库——`.puppy` 恢复的尺寸照常进仓库并建条目，
     * 进入该草图编辑时在此统一亮起。
     *
     * @param sketchId 编辑中草图的持久 ID；无会话传 null。
     */
    public setVisibleSketch(sketchId: string | null): void {
        this.visibleSketchId = sketchId;
        this.entryById.forEach((entry) => {
            this.applyEntryVisibility(entry);
        });
    }

    /**
     * 判断条目当前是否应显示。
     *
     * @param entry 渲染条目。
     * @returns 所属草图正在编辑、且条目未被 `display.hidden` 隐藏时返回 true。
     */
    private isEntryVisible(entry: SketchDimensionAnnotationEntry): boolean {
        // 三条独立显隐条件：草图会话可见 + 条目未隐藏（display.hidden）。
        return this.visibleSketchId !== null
            && entry.sketchId === this.visibleSketchId
            && !entry.hidden;
    }

    /**
     * 把显隐状态落到条目的全部渲染件（线/箭头/标签）。
     *
     * @param entry 渲染条目。
     */
    private applyEntryVisibility(entry: SketchDimensionAnnotationEntry): void {
        const visible = this.isEntryVisible(entry);

        entry.lines.visible = visible;
        entry.arrowMesh.visible = visible && entry.arrows.length > 0;
        // CSS2D 标签双保险：对象可见性 + DOM display（部分 three 版本遍历不看祖先）。
        entry.label.visible = visible;
        entry.element.style.display = visible ? '' : 'none';
        entry.auxiliaryLabels.forEach((auxiliary) => {
            auxiliary.visible = visible;
            (auxiliary.element as HTMLElement).style.display = visible ? '' : 'none';
        });
    }

    //------ 尺寸随草图会话显隐 结束------

    /** 缩放变化后按新屏幕尺寸重算全部箭头（不重建尺寸线/界线）。 */
    public refreshArrowScale(): void {
        this.entryById.forEach((entry) => {
            const color = (entry.arrowMesh.material as THREE.MeshBasicMaterial).color.getHexString();

            this.applyArrowMesh(entry, `#${color}`);
        });
    }

    /** 增量创建/更新单条尺寸标注。 */
    private upsertAnnotation(sketchId: string, dimensionId: string): void {
        const options = this.options;
        const snapshot = options?.repository.get(sketchId, dimensionId)?.toSnapshot() ?? null;
        const sketchGroup = options?.resolveSketchGroup(sketchId) ?? null;

        if (!options || !snapshot || !sketchGroup) {
            this.removeAnnotation(dimensionId);
            return;
        }

        const previewLabel = this.previewLabelById.get(dimensionId) ?? null;
        const layoutSnapshot: SketchDimensionSnapshot = previewLabel
            ? {...snapshot, display: {...snapshot.display, labelPosition: previewLabel}}
            : snapshot;
        /** Instant2D 预览值覆盖（只影响标签文本）。 */
        const previewValue = this.previewValueById.get(dimensionId);
        const textSnapshot: SketchDimensionSnapshot = previewValue !== undefined
            ? {...snapshot, parameter: {...snapshot.parameter, nominalValue: previewValue}}
            : snapshot;
        const anchors = this.resolveAnchors(snapshot, sketchGroup);
        const layout = resolveSketchDimensionLayout(layoutSnapshot, anchors);
        const referencePlane = sketchGroup.referencePlane;
        // 引用全部无法解析（目标实体已被删除等）：按悬空棕色展示（DANG-001 被动检测）。
        const referencesUnresolved = anchors.length > 0 && anchors.every((anchor) => anchor === undefined);
        const color = this.selectedDimensionIds.has(dimensionId)
            ? SELECTED_COLOR
            : resolveSketchDimensionStateColor(snapshot, {referencesUnresolved});

        let entry = this.entryById.get(dimensionId) ?? null;

        if (!entry) {
            const element = document.createElement('div');

            element.style.padding = '0 4px';
            element.style.borderRadius = '2px';
            element.style.fontSize = '12px';
            element.style.lineHeight = '18px';
            element.style.background = 'rgba(255, 255, 255, 0.88)';
            element.style.border = '1px solid transparent';
            element.style.whiteSpace = 'nowrap';
            element.style.userSelect = 'none';

            const valueElement = document.createElement('span');

            element.appendChild(valueElement);

            const label = new CSS2DObject(element);

            /**
             * 值文本默认在尺寸线**上方**（SW 默认摆位），而不是压在线上。
             * CSS2D 的 center 控制元素相对锚点的对齐：y=1 表示底边贴锚点，
             * 再加一点间隙让文字与线脱开；锚点仍是标注位置（拖标注语义不变）。
             */
            label.center.set(0.5, 1.18);
            const lineBuffer = new SketchDimensionGeometryBuffer();
            const arrowBuffer = new SketchDimensionGeometryBuffer();
            const lines = new THREE.LineSegments(lineBuffer.geometry, this.materialOf(color));
            const arrowMesh = new THREE.Mesh(arrowBuffer.geometry, this.arrowMaterialOf(color));

            lines.frustumCulled = false;
            lines.renderOrder = 998;
            arrowMesh.frustumCulled = false;
            arrowMesh.renderOrder = 998;
            this.group.add(lines);
            this.group.add(arrowMesh);
            this.group.add(label);
            entry = {
                sketchId,
                hidden: false,
                lines,
                lineBuffer,
                arrowMesh,
                arrowBuffer,
                arrows: [],
                referencePlane: null,
                label,
                element,
                valueElement,
                hitProxy: options.hitCallbacks
                    ? new SketchDimensionHitProxy(element, dimensionId, options.hitCallbacks)
                    : null,
                handleElement: null,
                disposeHandle: null,
                auxiliaryLabels: [],
            };
            this.entryById.set(dimensionId, entry);
        }
        entry.sketchId = sketchId;
        // 每次重算标注时刷新隐藏标记：仓库 updateDisplay 改 hidden 后无需重建条目即可显隐。
        entry.hidden = snapshot.display.hidden === true;

        // 线段缓冲：尺寸线/界线 + 角度弧采样，全部局部 → 世界（箭头单独走实心三角面）。
        const segments: SketchDimensionLayoutSegment[] = [...layout.segments];

        layout.arcs.forEach((arc) => {
            const sweep = arc.endAngle - arc.startAngle;

            for (let index = 0; index < ARC_SAMPLE_SEGMENTS; index += 1) {
                const fromAngle = arc.startAngle + (sweep * index) / ARC_SAMPLE_SEGMENTS;
                const toAngle = arc.startAngle + (sweep * (index + 1)) / ARC_SAMPLE_SEGMENTS;

                segments.push({
                    start: [
                        arc.center[0] + Math.cos(fromAngle) * arc.radius,
                        arc.center[1] + Math.sin(fromAngle) * arc.radius,
                    ],
                    end: [
                        arc.center[0] + Math.cos(toAngle) * arc.radius,
                        arc.center[1] + Math.sin(toAngle) * arc.radius,
                    ],
                });
            }
        });

        const position = entry.lineBuffer.beginUpdate(segments.length * 2);

        segments.forEach((segment, index) => {
            referencePlane.localToWorldTo(segment.start[0], segment.start[1], this.worldPoint);
            position.setXYZ(index * 2, this.worldPoint.x, this.worldPoint.y, this.worldPoint.z);
            referencePlane.localToWorldTo(segment.end[0], segment.end[1], this.worldPoint);
            position.setXYZ(index * 2 + 1, this.worldPoint.x, this.worldPoint.y, this.worldPoint.z);
        });
        entry.lines.geometry = entry.lineBuffer.endUpdate();
        entry.lines.material = this.materialOf(color);

        // 实心箭头（屏幕定长）：保存布局供缩放重算，再按当前尺寸写入三角面。
        entry.arrows = layout.arrows;
        entry.referencePlane = referencePlane;
        this.applyArrowMesh(entry, color);

        entry.valueElement.textContent = formatSketchDimensionDisplayValue(textSnapshot);
        entry.element.style.color = color;
        entry.element.style.border = this.selectedDimensionIds.has(dimensionId)
            ? `1px solid ${SELECTED_COLOR}`
            : '1px solid transparent';
        referencePlane.localToWorldTo(layout.textPosition[0], layout.textPosition[1], entry.label.position);
        this.syncAuxiliaryLabels(entry, layout, referencePlane, color);
        this.syncValueHandle(entry, snapshot, referencesUnresolved);
        // 显隐随会话：加载/撤销恢复期新建的条目也必须立即落到正确显隐态。
        this.applyEntryVisibility(entry);
    }

    /**
     * 同步辅助文本标签（尺寸链基准"0"等固定文案）。
     *
     * @param entry 渲染条目。
     * @param layout 布局。
     * @param referencePlane 参考平面。
     * @param color 文本颜色。
     */
    private syncAuxiliaryLabels(
        entry: SketchDimensionAnnotationEntry,
        layout: SketchDimensionLayout,
        referencePlane: NonNullable<SketchDimensionAnnotationEntry['referencePlane']>,
        color: string,
    ): void {
        const targets = layout.auxiliaryLabels ?? [];

        // 数量对齐：多余销毁、不足新建（复用既有 CSS2D 对象）。
        while (entry.auxiliaryLabels.length > targets.length) {
            const extra = entry.auxiliaryLabels.pop()!;

            extra.element.remove();
            extra.removeFromParent();
        }
        while (entry.auxiliaryLabels.length < targets.length) {
            const element = document.createElement('div');

            element.style.pointerEvents = 'none';
            element.style.fontSize = '11px';
            element.style.whiteSpace = 'nowrap';

            const auxiliary = new CSS2DObject(element);

            entry.lines.add(auxiliary);
            entry.auxiliaryLabels.push(auxiliary);
        }
        targets.forEach((target, index) => {
            const auxiliary = entry.auxiliaryLabels[index]!;

            (auxiliary.element as HTMLDivElement).textContent = target.text;
            (auxiliary.element as HTMLDivElement).style.color = color;
            auxiliary.position.copy(referencePlane.localToWorld(target.position[0], target.position[1]));
        });
    }

    /** 装配/拆除 Instant2D 值手柄（选中的 driving 尺寸且类型受支持时显示）。 */
    private syncValueHandle(
        entry: SketchDimensionAnnotationEntry,
        snapshot: SketchDimensionSnapshot,
        referencesUnresolved: boolean,
    ): void {
        const callbacks = this.options?.hitCallbacks;
        const needHandle = this.primarySelectedDimensionId === snapshot.dimensionId
            && this.selectedDimensionIds.size === 1
            && callbacks?.onValueHandleDragMove !== undefined
            && snapshot.role === 'driving'
            && !snapshot.suppressed
            && !snapshot.display.hidden
            && !snapshot.readOnly
            && snapshot.repair === null
            && !referencesUnresolved
            && isSketchDimensionInstant2dSupported(snapshot.dimensionKind);

        if (!needHandle) {
            entry.disposeHandle?.();
            entry.disposeHandle = null;
            entry.handleElement?.remove();
            entry.handleElement = null;
            return;
        }
        if (entry.handleElement) {
            return;
        }

        const handle = document.createElement('span');

        handle.style.display = 'inline-block';
        handle.style.width = '9px';
        handle.style.height = '9px';
        handle.style.marginLeft = '5px';
        handle.style.borderRadius = '50%';
        handle.style.background = '#1e6fff';
        handle.style.border = '1px solid #ffffff';
        handle.style.cursor = 'grab';
        handle.style.verticalAlign = 'middle';
        handle.style.pointerEvents = 'auto';

        const dimensionId = snapshot.dimensionId;
        let dragging = false;
        const handleMove = (event: PointerEvent): void => {
            dragging = true;
            callbacks?.onValueHandleDragMove?.(dimensionId, {x: event.clientX, y: event.clientY});
        };
        const stopListening = (): void => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
            window.removeEventListener('pointercancel', handleCancel);
            window.removeEventListener('blur', handleCancel);
            window.removeEventListener('keydown', handleKeyDown, true);
        };
        const handleCancel = (): void => {
            stopListening();
            if (!dragging) return;
            // 先终止监听所有权，回滚触发的标注更新可以安全地重入清理。
            dragging = false;
            callbacks?.onValueHandleDragCancel?.(dimensionId);
        };
        const handleUp = (event: PointerEvent): void => {
            stopListening();
            if (dragging) {
                dragging = false;
                callbacks?.onValueHandleDragCommit?.(dimensionId, {x: event.clientX, y: event.clientY});
            }
        };
        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') {
                return;
            }
            event.stopPropagation();
            event.preventDefault();
            handleCancel();
        };
        const handleDown = (event: PointerEvent): void => {
            if (event.button !== 0) {
                return;
            }
            event.stopPropagation();
            event.preventDefault();
            dragging = false;
            window.addEventListener('pointermove', handleMove);
            window.addEventListener('pointerup', handleUp);
            window.addEventListener('pointercancel', handleCancel);
            window.addEventListener('blur', handleCancel);
            window.addEventListener('keydown', handleKeyDown, true);
        };

        handle.addEventListener('pointerdown', handleDown);
        entry.element.appendChild(handle);
        entry.handleElement = handle;
        entry.disposeHandle = () => {
            handle.removeEventListener('pointerdown', handleDown);
            handleCancel();
        };
    }

    /** 移除单条尺寸标注。 */
    private removeAnnotation(dimensionId: string): void {
        const entry = this.entryById.get(dimensionId);

        if (!entry) {
            return;
        }
        // 取消拖动可能触发刷新或再次移除此条目，先解除索引以免同一几何被重复释放。
        this.entryById.delete(dimensionId);
        entry.hitProxy?.dispose();
        entry.disposeHandle?.();
        this.group.remove(entry.lines);
        entry.lineBuffer.dispose();
        this.group.remove(entry.arrowMesh);
        entry.arrowBuffer.dispose();
        this.group.remove(entry.label);
        entry.element.remove();
        entry.auxiliaryLabels.forEach((auxiliary) => {
            auxiliary.element.remove();
            auxiliary.removeFromParent();
        });
        entry.auxiliaryLabels.length = 0;
        this.previewLabelById.delete(dimensionId);
        this.previewValueById.delete(dimensionId);
        if (this.selectedDimensionIds.delete(dimensionId)) {
            this.primarySelectedDimensionId = this.primarySelectedDimensionId === dimensionId
                ? this.selectedDimensionIds.values().next().value ?? null
                : this.primarySelectedDimensionId;
        }
    }

    /**
     * 解析单个尺寸标注的屏幕包围矩形。
     *
     * @param entry 尺寸渲染条目。
     * @param camera 当前相机。
     * @param containerRect 渲染容器屏幕矩形。
     * @returns 相对容器的屏幕矩形；没有可投影内容时返回 null。
     */
    private resolveEntryScreenRect(
        entry: SketchDimensionAnnotationEntry,
        camera: THREE.Camera,
        containerRect: DOMRect,
    ): {minX: number; minY: number; maxX: number; maxY: number} | null {
        const points: Array<{x: number; y: number}> = [];
        const collectGeometryPoints = (object: THREE.LineSegments | THREE.Mesh): void => {
            const position = object.geometry.getAttribute('position');
            if (!position) {
                return;
            }
            const start = Math.max(0, object.geometry.drawRange.start);
            const end = Math.min(position.count, start + object.geometry.drawRange.count);
            for (let index = start; index < end; index += 1) {
                const world = new THREE.Vector3(position.getX(index), position.getY(index), position.getZ(index));
                const projected = world.project(camera);

                if (!Number.isFinite(projected.x) || !Number.isFinite(projected.y)) {
                    continue;
                }
                points.push({
                    x: ((projected.x + 1) / 2) * containerRect.width,
                    y: ((1 - projected.y) / 2) * containerRect.height,
                });
            }
        };

        collectGeometryPoints(entry.lines);
        if (entry.arrowMesh.visible) {
            collectGeometryPoints(entry.arrowMesh);
        }

        const labelRect = entry.element.getBoundingClientRect();
        if (labelRect.width > 0 || labelRect.height > 0) {
            points.push(
                {x: labelRect.left - containerRect.left, y: labelRect.top - containerRect.top},
                {x: labelRect.right - containerRect.left, y: labelRect.bottom - containerRect.top},
            );
        }
        if (points.length === 0) {
            return null;
        }

        return points.reduce((rect, point) => ({
            minX: Math.min(rect.minX, point.x),
            minY: Math.min(rect.minY, point.y),
            maxX: Math.max(rect.maxX, point.x),
            maxY: Math.max(rect.maxY, point.y),
        }), {
            minX: Number.POSITIVE_INFINITY,
            minY: Number.POSITIVE_INFINITY,
            maxX: Number.NEGATIVE_INFINITY,
            maxY: Number.NEGATIVE_INFINITY,
        });
    }

    /** 判断外层屏幕矩形是否完整包含内层矩形。 */
    private screenRectContains(
        outer: {readonly minX: number; readonly minY: number; readonly maxX: number; readonly maxY: number},
        inner: {readonly minX: number; readonly minY: number; readonly maxX: number; readonly maxY: number},
    ): boolean {
        return inner.minX >= outer.minX
            && inner.maxX <= outer.maxX
            && inner.minY >= outer.minY
            && inner.maxY <= outer.maxY;
    }

    /** 判断两个屏幕矩形是否相交。 */
    private screenRectsIntersect(
        left: {readonly minX: number; readonly minY: number; readonly maxX: number; readonly maxY: number},
        right: {readonly minX: number; readonly minY: number; readonly maxX: number; readonly maxY: number},
    ): boolean {
        return left.minX <= right.maxX
            && left.maxX >= right.minX
            && left.minY <= right.maxY
            && left.maxY >= right.minY;
    }

    /** 释放全部资源。 */
    public dispose(): void {
        this.detach();
    }
}
